import * as Yup from "yup"
import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"

import { useForm, yupResolver } from "@mantine/form"
import {
  TextInput,
  Textarea,
  Select,
  createStyles,
  LoadingOverlay,
  rem,
  Checkbox,
  Button,
  Box,
  Group,
  Stack,
  Title, Alert, Text,
} from "@mantine/core"
import { IconAlertCircle } from "@tabler/icons-react"

import { observer } from "mobx-react-lite"
import { TextEditor } from "../../../entities/textEditor"
import { categoryNames } from "../../../shared/lib/constants"
import { ImageDropzone } from "../../../shared/ui/ImageDropzone"
import { type Article } from "../../../shared/types/article"
import { PATHS } from "../../../shared/lib/paths"
import { ArticleStore } from "../../../app/store"
import { selectValues } from "./config"

// ----------------------------------------------------------------------

const useStyles = createStyles((theme) => ({
  title: {
    paddingBottom: theme.spacing.md,
    marginBottom: `calc(${theme.spacing.md} * 1.5)`,
    borderBottom: `${rem(1)} solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]}`,

    [theme.fn.smallerThan("sm")]: {
      borderBottom: 0,
    },
  },
}))

const schema = Yup.object().shape({
  title: Yup.string().required("Название статьи обязательно"),
  description: Yup.string().required("Оставьте краткое описание для удобства пользователя"),
  textContent: Yup.string(),
  published: Yup.boolean().required(),
  cover: Yup.string().required(),
  keywords: Yup.string().optional(),
  category: Yup.string().oneOf([ ...Object.keys(categoryNames) ]).required("Выберите категорию для статьи"),
})

export const ArticleEdit = observer(function ArticleEdit() {
  const { classes } = useStyles()
  const navigate = useNavigate()

  const [ initialTextContent, setInitialTextContent ] = useState<string>("")

  const { articleId } = useParams<{ articleId?: string }>()
  const { createArticle, updateArticle, loadArticle, error, isLoading } = ArticleStore

  const form = useForm<Partial<Article>>({
    validateInputOnChange: true,
    validate: yupResolver(schema),
    initialValues: {
      title: "",
      description: "",
      textContent: "",
      published: true,
      cover: undefined,
      category: "nutriciology",
      keywords: "",
    },
  })

  useEffect(() => {
    if (articleId) {
      void loadArticle(articleId).then((article) => {
        if (article) {
          setInitialTextContent(article.textContent)

          form.setValues(article)
          form.resetDirty(article)
        }
      })
    }

    return () => {
      form.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ articleId ])

  const onSubmit = (values: typeof form.values): void => {
    const formData = new FormData()
    const { _id, createdAt, updatedAt, ...dto } = values

    Object.entries(dto).forEach(([ key, value ]) => {
      if (articleId && form.isDirty(key) || !articleId) {
        switch (key) {
          case "published": {
            formData.append(key, String(value))
            break
          }
          default: {
            formData.append(key, value as string | File)
            break
          }
        }
      }
    })

    if (articleId) {
      void updateArticle(articleId, formData).then(success => {
        if (success) {
          navigate(PATHS.DASHBOARD.BLOG.ROOT)
        }
      })
    } else {
      void createArticle(formData).then(success => {
        if (success) {
          navigate(PATHS.DASHBOARD.BLOG.ROOT)
        }
      })
    }
  }

  return (
    <Box w="100%" pos="relative">
      <LoadingOverlay visible={isLoading} overlayBlur={2}/>

      <Title order={1} size={24} h={50} className={classes.title}>
        {articleId ? "Редактирование статьи" : "Создание статьи"}
      </Title>

      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack spacing="xl">
          <TextInput label="Название статьи" placeholder="Новая статья" {...form.getInputProps("title")} />

          <Textarea
            label="Краткое описание"
            placeholder="Основная суть в 3 строчках"
            {...form.getInputProps("description")}
            minRows={3}
            autosize
          />

          <Stack spacing={0}>
            <Text size="sm">
              Текст статьи
            </Text>
            <TextEditor
              initialValue={initialTextContent}
              placeholder="Напишите здесь текст новой статьи..."
              {...form.getInputProps("textContent")}
            />
          </Stack>

          <Stack spacing={4}>
            <Textarea
              label="Ключевые слова"
              placeholder="Перечистели их через запятую. Если поле останется пустым, то ключевые слова буду выбраны из статьи автоматически, но результат может быть непредсказуем"
              {...form.getInputProps("keywords")}
              minRows={3}
              autosize
            />
          </Stack>

          <Select label="Категория" data={selectValues} {...form.getInputProps("category")} />

          <ImageDropzone formValues={form.getInputProps("cover")}/>

          <Checkbox label="Опубликовать статью" {...form.getInputProps("published", { type: "checkbox" })} />
        </Stack>

        {error && (
          <Alert mt="xl" icon={<IconAlertCircle size="1rem"/>} title="Произошла ошибка" color="red">
            {error}
          </Alert>
        )}

        <Group position="center" my="xl">
          <Button disabled={!form.isDirty() || !form.isValid()} type="submit">
            {articleId ? "Обновить" : "Создать"}
          </Button>
          <Button disabled={isLoading} variant="light" color="gray" component={Link} to={PATHS.DASHBOARD.BLOG.ROOT}>
            Назад
          </Button>
        </Group>
      </form>
    </Box>
  )
})
